// Lib
import React from 'react';
import { ConnectedProps, connect } from 'react-redux';
import { difference } from 'lodash';
import memoizeOne from 'memoize-one';

// Utils
import { getSnappedSourceClientOffsetFromDragAndDropSingleton } from '../../../element/dnd/dragAndDropStateSingleton';
import * as pointsLib from '../../../../common/maths/geometry/point';

// Components
import ConnectedLineDragPreview from '../../../element/line/dragPreview/ConnectedLineDragPreview';
import ElementPositioner from '../../../element/ElementPositioner';

// Selector
import { getDragModifierKeys } from '../../../utils/dnd/modifierKeys/dragModifierKeysSelector';

// Constants
import { MODIFIER_KEYS } from '../../../utils/dnd/modifierKeys/dragModifierKeysConstants';

// Types
import { Point } from '../../../../common/maths/geometry/pointTypes';
import { LineDragLayerProps } from '../dragTypes';

/**
 * memoizeOne memoizes only the last invocation of the method, thus ensuring the memoize cache doesn't grow out of
 * control.
 */
const getNotDraggedConnectedElementIds = memoizeOne((connectingElementIds, draggedElementIds): string[] =>
    difference(connectingElementIds, draggedElementIds),
);

const connectDraggedConnectedElement = connect((state) => ({
    copyDrag: getDragModifierKeys(state).get(MODIFIER_KEYS.altKey),
}));

const ORIGIN = { x: 0, y: 0 };

const getSnappedOffset = (offsetDiff: Point, initialSourceClientOffset: Point, copyDrag: boolean): Point => {
    // FIXME Hack to keep lines in place when the altKey is pressed (as the element is being copied, not moved)
    // This should probably be moved into the dnd library somehow
    if (copyDrag) return ORIGIN;
    // Account for the dragged element being shift-snapped to horizontal or vertical.
    const snappedOffset = getSnappedSourceClientOffsetFromDragAndDropSingleton() as Point | null;
    return snappedOffset ? pointsLib.difference(initialSourceClientOffset, snappedOffset) : offsetDiff;
};

export const DraggedConnectedElement = connectDraggedConnectedElement(
    ({
        item,
        offsetDiff,
        initialSourceClientOffset,
        scrollDiff,
        gridSize,
        copyDrag,
    }: LineDragLayerProps & ConnectedProps<typeof connectDraggedConnectedElement>) => {
        const offset = pointsLib.translate(
            getSnappedOffset(offsetDiff, initialSourceClientOffset, copyDrag),
            item.scaledCustomDragOffset,
        );

        // If a connected element is also selected then it will also be dragged so shouldn't be included in the connected
        // element drag preview (but instead in the selected element drag preview).
        const notDraggedConnectedElementIds = getNotDraggedConnectedElementIds(
            item.connectingElementIds,
            item.draggedElementIds,
        );

        return (
            <div className="DraggedConnectedElement">
                {notDraggedConnectedElementIds.map((connectingElementId) => (
                    <ElementPositioner key={connectingElementId} elementId={connectingElementId}>
                        <ConnectedLineDragPreview
                            elementId={connectingElementId}
                            draggedElementIds={item.draggedElementIds}
                            draggedDescendantIds={item.draggedDescendantIds}
                            offsetDiff={offset}
                            scrollDiff={scrollDiff}
                            gridSize={gridSize}
                        />
                    </ElementPositioner>
                ))}
            </div>
        );
    },
);
